/**
 * ArrivalsPage.js
 *
 * /fly/arrivals — the split-flap arrivals board for the TV at the party, matching arrivals-board.html.
 * Public: TIME, FROM, COUNTRY and STATUS only (no names or seats), with a stripe in each guest's colour.
 * It can also be the counter: with the staff key entered here, a USB/Bluetooth 2D scanner on this computer marks each
 * scanned boarding pass as landed, so one laptop on the TV both checks guests in and shows them arriving.
 * Polls the backend every 2 seconds, rotates pages of 9 rows every 9 seconds, and flips the banner to
 * "JUST LANDED · <CITY>" for 7 seconds when someone checks in at the counter.
 * Built to run for hours: every timer and animation is cleaned up, and it reconnects by itself.
 */

import React, { useEffect, useRef, useState } from "react";
import { KeyRound } from "lucide-react";
import { getArrivals, markArrived } from "./helpers/flyApi";
import { beep, formatTime, useBarcodeScanner, useNoIndex, useStoredKey } from "./helpers/flyPage";

const POLL_MS = 2000;
const MAX_RETRY_MS = 15000;
const ROWS_PER_PAGE = 9;
const PAGE_MS = 9000;
const JUST_LANDED_MS = 7000;
const FLIP_MS = 40;

// Columns and their widths in flaps; the banner spans all of them
const COLUMNS = [["Time", 5], ["From", 14], ["Country", 12], ["Status", 8]];
const BANNER_LENGTH = COLUMNS.reduce((total, [, width]) => total + width, 0) + 2;
// The order a flap turns through on its way to the next character
const CHARS = " ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789:·-,.";
const BANNER = "YJ030 · DRINK. EAT. FLY.";
const BANNER_CLOSED = "ARRIVALS CLOSED";

export const PLANE_PATH = "M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z";

// How long a scan's result stays in the board's footer
const SCAN_RESULT_MS = 5000;

// This board has a Country column and fills the screen's width, so its flaps are sized here, not by the shared style
// (the TV game's board)
const WIDE_STYLES = `
/* The whole width of the screen: the flaps are sized so the board's 41 flaps across fill it (or the height, if that's tighter) */
.yj-arrivals.with-country{--cw:min(max(12px,calc((100vw - 124px) / 43.6)),calc((100vh - 48px) / 23))}
.yj-arrivals.with-country .wrap{max-width:none}
.yj-arrivals.with-country .board:fullscreen{--cw:min(calc((100vw - 92px) / 43.6),calc(100vh / 23.5))}
.yj-arrivals .scan-ok{color:var(--landed)}
.yj-arrivals .scan-bad{color:var(--delayed)}
`;

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// arrivals-board.html's styles, scoped to this page
export const STYLES = `
.yj-arrivals{
  --hall:#0E0F12;--board:#16171B;--flap:#232429;--flap-edge:#0A0A0C;--ink:#EFEADF;--ink-dim:#8E8A82;
  --landed:#F2B705;--delayed:#FF6A55;--diverted:#6E6B66;--line:#2C2D33;
  --f-flap:"Oswald","Arial Narrow",Impact,sans-serif;
  --f-ui:"IBM Plex Sans Condensed","Arial Narrow",system-ui,sans-serif;
  --f-mono:"IBM Plex Mono",ui-monospace,Menlo,monospace;
  /* The design's flap width, also capped by height so the whole board fits a TV screen without scrolling */
  --cw:min(clamp(15px,calc((min(100vw,1500px) - 200px) / 34),46px),calc((100vh - 48px) / 23));
  color-scheme:dark;min-height:100vh;background:var(--hall);color:var(--ink);font-family:var(--f-ui);text-align:left;
  padding:24px max(16px,env(safe-area-inset-right,0px)) 40px max(16px,env(safe-area-inset-left,0px));
}
.yj-arrivals *{box-sizing:border-box}
.yj-arrivals .wrap{max-width:1500px;margin:0 auto;display:flex;flex-direction:column;gap:20px}
.yj-arrivals .board-scroll{overflow-x:auto;border-radius:10px}
.yj-arrivals .board{min-width:max-content;background:var(--board);border:1px solid var(--line);border-radius:10px;padding:calc(var(--cw)*.8);display:flex;flex-direction:column;gap:calc(var(--cw)*.42);box-shadow:0 30px 60px -30px #000}
.yj-arrivals .board:fullscreen{display:flex;justify-content:center;border-radius:0;border:0;--cw:min(calc((100vw - 240px) / 34),calc(100vh / 23.5))}
.yj-arrivals .bhead{display:flex;align-items:flex-end;justify-content:space-between;gap:24px}
.yj-arrivals .btitle{display:flex;align-items:center;gap:calc(var(--cw)*.5)}
.yj-arrivals .btitle svg{width:calc(var(--cw)*1.7);height:calc(var(--cw)*1.7);color:var(--landed);transform:rotate(45deg)}
.yj-arrivals .btitle .big{font-family:var(--f-flap);font-weight:600;font-size:calc(var(--cw)*1.55);letter-spacing:.08em;line-height:1;color:var(--ink)}
.yj-arrivals .btitle .sub{font-family:var(--f-ui);font-size:calc(var(--cw)*.42);letter-spacing:.2em;color:var(--ink-dim);margin-top:4px;text-transform:uppercase}
.yj-arrivals .clockwrap{display:flex;flex-direction:column;align-items:flex-end;gap:4px}
.yj-arrivals .lbl{font-family:var(--f-ui);font-weight:600;font-size:calc(var(--cw)*.36);letter-spacing:.2em;color:var(--ink-dim);text-transform:uppercase}
.yj-arrivals .row{display:flex;align-items:center;gap:calc(var(--cw)*.5)}
.yj-arrivals .rows{display:flex;flex-direction:column;gap:calc(var(--cw)*.22)}
.yj-arrivals .grp{display:flex;gap:2px}
.yj-arrivals .swatch{width:calc(var(--cw)*.28);align-self:stretch;border-radius:2px;background:transparent;transition:background .4s}
.yj-arrivals .cols{display:flex;gap:calc(var(--cw)*.5);padding-left:calc(var(--cw)*.78)}
.yj-arrivals .cols span{font-family:var(--f-ui);font-weight:600;font-size:calc(var(--cw)*.36);letter-spacing:.2em;color:var(--ink-dim);text-transform:uppercase}
.yj-arrivals .cell{position:relative;width:var(--cw);height:calc(var(--cw)*1.42);perspective:calc(var(--cw)*8);font-family:var(--f-flap);font-weight:500;font-size:calc(var(--cw)*1.02);color:var(--ink)}
.yj-arrivals .h{position:absolute;left:0;right:0;height:50%;overflow:hidden;background:var(--flap);border-radius:3px}
.yj-arrivals .h b{position:absolute;left:0;right:0;height:200%;display:flex;align-items:center;justify-content:center;font-weight:inherit;line-height:1}
.yj-arrivals .t,.yj-arrivals .ft{top:0;border-bottom:1px solid var(--flap-edge);border-bottom-left-radius:0;border-bottom-right-radius:0;transform-origin:50% 100%}
.yj-arrivals .b,.yj-arrivals .fb{bottom:0;border-top-left-radius:0;border-top-right-radius:0;transform-origin:50% 0}
.yj-arrivals .t b,.yj-arrivals .ft b{top:0}
.yj-arrivals .b b,.yj-arrivals .fb b{bottom:0}
.yj-arrivals .b{background:linear-gradient(var(--flap),color-mix(in srgb,var(--flap) 85%,#000))}
.yj-arrivals .ft,.yj-arrivals .fb{visibility:hidden;backface-visibility:hidden;z-index:2}
.yj-arrivals .grp.landed .cell{color:var(--landed)}
.yj-arrivals .grp.delayed .cell{color:var(--delayed)}
.yj-arrivals .grp.diverted .cell{color:var(--diverted)}
.yj-arrivals .grp.expected .cell{color:var(--ink)}
.yj-arrivals .banner .cell{color:var(--landed)}
.yj-arrivals .bfoot{display:flex;justify-content:space-between;align-items:center;gap:16px;font-family:var(--f-mono);font-size:calc(var(--cw)*.36);letter-spacing:.12em;color:var(--ink-dim);text-transform:uppercase;padding-left:calc(var(--cw)*.78)}
.yj-arrivals .offline{color:var(--delayed)}
.yj-arrivals .dots{display:flex;gap:6px}
.yj-arrivals .dots i{width:8px;height:8px;border-radius:50%;background:var(--line)}
.yj-arrivals .dots i.on{background:var(--landed)}
.yj-arrivals .btns{display:flex;flex-wrap:wrap;gap:8px}
.yj-arrivals button{font-family:var(--f-ui);font-weight:600;font-size:15px;letter-spacing:.06em;text-transform:uppercase;border-radius:6px;padding:12px 16px;border:1px solid var(--line);background:var(--hall);color:var(--ink);cursor:pointer;min-height:44px}
.yj-arrivals button:hover{border-color:var(--ink-dim)}
.yj-arrivals button:focus-visible{outline:2px solid var(--landed);outline-offset:2px}
@media (prefers-reduced-motion:reduce){.yj-arrivals .swatch{transition:none}}
`;

/**
 * Text as flaps can show it: uppercase, accents dropped (São Paulo → SAO PAULO), cut or padded to the width
 */
export const fit = (text, width) => (text || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().slice(0, width).padEnd(width, " ");

/**
 * Centred in the width, like the design's banner
 */
const centre = (text, width) => {
    const value = fit(text, width).trimEnd();
    return " ".repeat(Math.floor((width - value.length) / 2)) + value;
};

/**
 * The flap click from arrivals-board.html: a burst of filtered noise, at most every 22ms. Off until tapped on.
 */
export const useFlapSound = () => {
    const audioRef = useRef(null);
    const noiseRef = useRef(null);
    const lastRef = useRef(0);
    const onRef = useRef(false);
    const [on, setOn] = useState(false);

    const toggle = () => {
        if (!audioRef.current) {
            try {
                const audio = new (window.AudioContext || window.webkitAudioContext)();
                const length = Math.floor(audio.sampleRate * 0.012);
                const noise = audio.createBuffer(1, length, audio.sampleRate);
                const data = noise.getChannelData(0);
                for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 3;
                audioRef.current = audio;
                noiseRef.current = noise;
            } catch {
                return;
            }
        }
        if (audioRef.current.state === "suspended") audioRef.current.resume();
        onRef.current = !onRef.current;
        setOn(onRef.current);
    };

    // Stable, so flaps can hold on to it
    const clickRef = useRef(() => {
        const audio = audioRef.current;
        if (!onRef.current || !audio) return;
        const now = audio.currentTime;
        if (now - lastRef.current < 0.022) return;
        lastRef.current = now;
        const source = audio.createBufferSource();
        source.buffer = noiseRef.current;
        const gain = audio.createGain();
        gain.gain.value = 0.12;
        const filter = audio.createBiquadFilter();
        filter.type = "bandpass";
        filter.frequency.value = 2400 + Math.random() * 900;
        source.connect(filter).connect(gain).connect(audio.destination);
        source.start(now);
    });

    useEffect(() => () => { audioRef.current?.close().catch(() => {}); }, []);

    return { on, toggle, click: clickRef.current };
};

/**
 * One split-flap character. The top half falls away and the bottom half swings down, a few characters
 * at a time on the way to the new one, like the design. The flaps are driven directly, outside React.
 * @param {String} char     -   The character to show
 * @param {Number} delay    -   Wait before starting, in ms, so a row ripples left to right
 * @param {Boolean} instant -   Show it straight away (the board's first paint, like the design)
 * @param {Function} click  -   The flap sound
 */
const FlapCell = ({ char, delay, instant, click }) => {
    const topRef = useRef(null);
    const bottomRef = useRef(null);
    const fallingRef = useRef(null);
    const risingRef = useRef(null);
    const stateRef = useRef({ current: " ", target: " ", busy: false, alive: true });

    useEffect(() => {
        const state = stateRef.current;
        state.alive = true;
        return () => { state.alive = false; };
    }, []);

    useEffect(() => {
        const state = stateRef.current;
        const [top, bottom, falling, rising] = [topRef.current, bottomRef.current, fallingRef.current, risingRef.current];

        const paint = (value) => {
            top.firstChild.textContent = value;
            bottom.firstChild.textContent = value;
            state.current = value;
        };

        const flip = async (from, to) => {
            top.firstChild.textContent = to;
            bottom.firstChild.textContent = from;
            falling.firstChild.textContent = from;
            rising.firstChild.textContent = to;
            falling.style.visibility = "visible";
            rising.style.visibility = "visible";
            rising.style.transform = "rotateX(90deg)";
            click();
            // An animation that never finishes (a stalled or hidden page) mustn't freeze the flap
            const settle = (animation) => Promise.race([animation.finished, new Promise((resolve) => setTimeout(resolve, FLIP_MS * 4))]);
            try {
                await settle(falling.animate([{ transform: "rotateX(0deg)" }, { transform: "rotateX(-90deg)" }], { duration: FLIP_MS, easing: "ease-in", fill: "forwards" }));
                falling.style.visibility = "hidden";
                await settle(rising.animate([{ transform: "rotateX(90deg)" }, { transform: "rotateX(0deg)" }], { duration: FLIP_MS, easing: "ease-out", fill: "forwards" }));
            } catch {
                // Cancelled: the end state below is still right
            }
            bottom.firstChild.textContent = to;
            rising.style.visibility = "hidden";
            falling.getAnimations().forEach((animation) => animation.cancel());
            rising.getAnimations().forEach((animation) => animation.cancel());
            state.current = to;
        };

        // Turns through at most 5 characters to reach the target, whatever it changes to meanwhile
        const run = async () => {
            if (state.busy) return;
            state.busy = true;
            if (delay) await new Promise((resolve) => setTimeout(resolve, delay));
            while (state.alive && state.current !== state.target) {
                const target = state.target;
                const length = CHARS.length;
                const from = Math.max(0, CHARS.indexOf(state.current));
                const to = CHARS.indexOf(target);
                if (to < 0) {
                    paint(target);
                    break;
                }
                const steps = Math.min((to - from + length) % length, 5);
                await flip(state.current, CHARS[(to - (steps - 1) + length) % length]);
            }
            state.busy = false;
        };

        state.target = char;
        if (instant || reducedMotion() || !("animate" in top)) paint(char);
        else if (state.current !== char) run();
    }, [char, delay, instant, click]);

    return (
        <div className="cell" aria-hidden="true">
            <span className="h t" ref={topRef}><b> </b></span>
            <span className="h b" ref={bottomRef}><b> </b></span>
            <span className="h ft" ref={fallingRef}><b> </b></span>
            <span className="h fb" ref={risingRef}><b> </b></span>
        </div>
    );
};

/**
 * A run of flaps showing `text` (already fitted to its width)
 * @param {Number} baseDelay    -   Wait before the first flap, in ms
 */
export const FlapGroup = ({ text, className = "", baseDelay = 0, instant = false, click }) => (
    <div className={`grp ${className}`} role="img" aria-label={text.trim() || "blank"}>
        {[...text].map((char, i) => <FlapCell key={i} char={char} delay={baseDelay + i * 14} instant={instant} click={click} />)}
    </div>
);

/**
 * The time in IST, as HH:MM
 */
const useClock = () => {
    const [time, setTime] = useState(() => formatTime(new Date().toISOString()));
    useEffect(() => {
        const timer = setInterval(() => setTime(formatTime(new Date().toISOString())), 1000);
        return () => clearInterval(timer);
    }, []);
    return time;
};

/**
 * Keeps the screen (and so the laptop or TV) awake while the page is open. The browser can drop the lock at any time
 * (tab hidden, battery saver, display change), and some only grant it after a click or key press, so it asks again
 * whenever the lock is lost, the tab comes back, the page is clicked or a pass is scanned, and every 30 seconds.
 */
export const useWakeLock = () => {
    useEffect(() => {
        let lock = null;
        let pending = false;
        let released = false;
        const request = async () => {
            if (released || pending || (lock && !lock.released)) return;
            if (!("wakeLock" in navigator) || document.visibilityState !== "visible") return;
            pending = true;
            try {
                const next = await navigator.wakeLock.request("screen");
                if (released) next.release().catch(() => {});
                else {
                    lock = next;
                    lock.addEventListener("release", () => { setTimeout(request, 1000); });
                }
            } catch {
                // Not allowed right now (e.g. no click yet, battery saver): asked again on the next click, scan or tick
            } finally {
                pending = false;
            }
        };
        const onVisible = () => { if (document.visibilityState === "visible") request(); };
        request();
        const timer = setInterval(request, 30000);
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("pointerdown", request);
        window.addEventListener("keydown", request);
        return () => {
            released = true;
            clearInterval(timer);
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("pointerdown", request);
            window.removeEventListener("keydown", request);
            lock?.release().catch(() => {});
        };
    }, []);
};

/**
 * The board's data, polled every 2 seconds. Waits longer after failures (up to 15s) and recovers by itself.
 * @returns {Object}    -   { board (last good data, or null), offline }
 */
const useArrivals = () => {
    const [board, setBoard] = useState(null);
    const pokeRef = useRef(() => {});
    const [offline, setOffline] = useState(false);
    useEffect(() => {
        let stopped = false;
        let timer;
        let failures = 0;
        const poll = async () => {
            try {
                const data = await getArrivals();
                if (stopped) return;
                failures = 0;
                setBoard(data);
                setOffline(false);
            } catch {
                if (stopped) return;
                failures += 1;
                if (failures >= 3) setOffline(true);
            }
            if (!stopped) timer = setTimeout(poll, Math.min(POLL_MS * 2 ** Math.max(0, failures - 1), MAX_RETRY_MS));
        };
        pokeRef.current = () => { clearTimeout(timer); poll(); };
        poll();
        return () => {
            stopped = true;
            clearTimeout(timer);
        };
    }, []);
    return { board, offline, refresh: () => pokeRef.current() };
};

const ArrivalsPage = () => {
    const { board, offline, refresh } = useArrivals();
    const sound = useFlapSound();
    const clock = useClock();
    const boardRef = useRef(null);
    const [page, setPage] = useState(0);
    const [justLanded, setJustLanded] = useState(null);
    // The first board is painted straight away; only changes after that flip
    const [settled, setSettled] = useState(false);
    const latestRef = useRef(undefined);
    const lastTurnRef = useRef(Date.now());
    const justLandedRef = useRef(false);

    useNoIndex("Arrivals · BAH");
    useWakeLock();

    // ---- Scanning boarding passes here (staff key), so this screen is also the counter ----
    const [key, saveKey, forgetKey] = useStoredKey("yj030-staff-key");
    const [askingKey, setAskingKey] = useState(false);
    const [typedKey, setTypedKey] = useState("");
    // The last scan's result, in the footer for a few seconds: { ok, text }
    const [scanResult, setScanResult] = useState(null);
    const audioRef = useRef(null);
    const scanTimerRef = useRef(null);
    const lastScanRef = useRef({ code: "", at: 0 });
    // One scan at a time, in order: none is dropped when guests are scanned back to back
    const scansRef = useRef(Promise.resolve());
    useEffect(() => () => clearTimeout(scanTimerRef.current), []);

    const showScan = (ok, text) => {
        setScanResult({ ok, text });
        beep(audioRef.current, ok);
        clearTimeout(scanTimerRef.current);
        scanTimerRef.current = setTimeout(() => setScanResult(null), SCAN_RESULT_MS);
    };

    const checkIn = async (code) => {
        try {
            const { guest, already } = await markArrived(key, code);
            const firstName = guest.fullName.split(" ")[0];
            if (already) showScan(false, `${firstName} already landed at ${formatTime(guest.arrivedAt)}`);
            else showScan(true, `Welcome, ${firstName} · ${guest.fromCity}`);
            refresh();
        } catch (err) {
            if (err.status === 401) {
                forgetKey();
                setAskingKey(true);
                showScan(false, "That staff key didn't work");
            } else {
                showScan(false, err.status === 404 ? "Unknown boarding pass" : "Couldn't reach the server. Scan again.");
            }
        }
    };

    useBarcodeScanner((code) => {
        const now = Date.now();
        // The same pass twice in a row (a double read) counts once
        if (code === lastScanRef.current.code && now - lastScanRef.current.at < 6000) return;
        lastScanRef.current = { code, at: now };
        try {
            audioRef.current = audioRef.current || new (window.AudioContext || window.webkitAudioContext)();
            audioRef.current.resume();
        } catch {
            // No sound is fine
        }
        scansRef.current = scansRef.current.then(() => checkIn(code));
    }, !!key);

    const rows = board?.rows || [];
    const pageCount = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
    const pageCountRef = useRef(pageCount);
    pageCountRef.current = pageCount;
    justLandedRef.current = justLanded !== null;

    useEffect(() => {
        if (!board || settled) return undefined;
        const timer = setTimeout(() => setSettled(true), 100);
        return () => clearTimeout(timer);
    }, [board, settled]);

    // Turn the page every 9 seconds, but not while JUST LANDED is up
    useEffect(() => {
        const timer = setInterval(() => {
            if (Date.now() - lastTurnRef.current < PAGE_MS || justLandedRef.current) return;
            lastTurnRef.current = Date.now();
            setPage((current) => (current + 1) % pageCountRef.current);
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // A new arrival flips the banner to JUST LANDED for 7 seconds and shows the first page, where the newest arrivals are.
    // Whoever was latest when the board opened doesn't count as new.
    // The timer lives in a ref: the next poll (2 seconds later) mustn't cancel it, or JUST LANDED stays up and the
    // pages stop turning.
    const justLandedTimerRef = useRef(null);
    useEffect(() => () => clearTimeout(justLandedTimerRef.current), []);
    useEffect(() => {
        if (!board) return;
        const key = board.latest ? `${board.latest.arrivedAt}|${board.latest.fromCity}` : null;
        const isNew = latestRef.current !== undefined && key && key !== latestRef.current;
        latestRef.current = key;
        if (!isNew) return;
        setJustLanded(board.latest.fromCity);
        setPage(0);
        lastTurnRef.current = Date.now();
        clearTimeout(justLandedTimerRef.current);
        justLandedTimerRef.current = setTimeout(() => {
            setJustLanded(null);
            lastTurnRef.current = Date.now();
        }, JUST_LANDED_MS);
    }, [board]);

    const shownPage = page % pageCount;
    const pageRows = rows.slice(shownPage * ROWS_PER_PAGE, (shownPage + 1) * ROWS_PER_PAGE);
    // Always 9 rows, so the board never changes size
    const paddedRows = [...pageRows, ...Array(ROWS_PER_PAGE - pageRows.length).fill(null)];
    const banner = justLanded ? `JUST LANDED · ${justLanded}` : board?.closed ? BANNER_CLOSED : BANNER;

    const fullScreen = () => {
        try {
            boardRef.current.requestFullscreen?.().catch(() => {});
        } catch {
            // Not available (e.g. inside an app's browser)
        }
    };

    return (
        <div className="yj-arrivals with-country">
            <style>{STYLES}{WIDE_STYLES}</style>
            <div className="wrap">
                <div className="board-scroll">
                    <section className="board" ref={boardRef} aria-label="Arrivals board">
                        <div className="bhead">
                            <div className="btitle">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={PLANE_PATH} /></svg>
                                <div>
                                    <h1 className="big">ARRIVALS</h1>
                                    <div className="sub">BAH · Bahubali Enclave · Right Brother Airways</div>
                                </div>
                            </div>
                            <div className="clockwrap">
                                <span className="lbl">Local time</span>
                                <FlapGroup text={clock} instant={!settled} click={sound.click} />
                            </div>
                        </div>

                        <div className="row banner">
                            <span className="swatch" />
                            <FlapGroup text={centre(banner, BANNER_LENGTH).padEnd(BANNER_LENGTH, " ")} instant={!settled} click={sound.click} />
                        </div>

                        <div className="cols" aria-hidden="true">
                            {COLUMNS.map(([name, width]) => <span key={name} style={{ width: `calc(var(--cw) * ${width} + ${(width - 1) * 2}px)` }}>{name}</span>)}
                        </div>

                        <div className="rows">
                            {paddedRows.map((row, i) => {
                                const status = row?.status.toLowerCase() || "";
                                const landed = status === "landed";
                                return (
                                    <div className="row" key={i}>
                                        <span className="swatch" style={{ background: row?.colour || "transparent" }} />
                                        <FlapGroup text={fit(row ? (landed ? formatTime(row.arrivedAt) : "--:--") : "", COLUMNS[0][1])} className={landed ? "landed" : ""} baseDelay={i * 40} instant={!settled} click={sound.click} />
                                        <FlapGroup text={fit(row?.fromCity, COLUMNS[1][1])} baseDelay={i * 40} instant={!settled} click={sound.click} />
                                        <FlapGroup text={fit(row?.country, COLUMNS[2][1])} baseDelay={i * 40} instant={!settled} click={sound.click} />
                                        <FlapGroup text={fit(row?.status, COLUMNS[3][1])} className={status} baseDelay={i * 40} instant={!settled} click={sound.click} />
                                    </div>
                                );
                            })}
                        </div>

                        <div className="bfoot">
                            {scanResult ? (
                                <span className={scanResult.ok ? "scan-ok" : "scan-bad"} role="status">{scanResult.ok ? "✓" : "✗"} {scanResult.text}</span>
                            ) : (
                                <span>
                                    {board ? `${board.landed} of ${board.total} landed · page ${shownPage + 1}/${pageCount}` : "Connecting…"}
                                    {key && " · Scanner ready"}
                                    {offline && <span className="offline"> · Reconnecting…</span>}
                                </span>
                            )}
                            <span className="dots" aria-hidden="true">
                                {Array.from({ length: pageCount }, (_, i) => <i key={i} className={i === shownPage ? "on" : ""} />)}
                            </span>
                        </div>
                    </section>
                </div>

                <div className="btns">
                    <button type="button" onClick={sound.toggle} aria-pressed={sound.on}>Flap sound: {sound.on ? "on" : "off"}</button>
                    <button type="button" onClick={fullScreen}>Full screen</button>
                    {key
                        ? <button type="button" onClick={forgetKey}>Stop scanning here</button>
                        : <button type="button" onClick={() => setAskingKey(true)}><KeyRound size={14} style={{ display: "inline", marginRight: 6 }} aria-hidden="true" />Scan passes here</button>}
                </div>
                {askingKey && !key && (
                    <form className="btns" onSubmit={(e) => { e.preventDefault(); if (typedKey.trim()) { saveKey(typedKey.trim()); setAskingKey(false); setTypedKey(""); } }}>
                        <input type="password" value={typedKey} onChange={(e) => setTypedKey(e.target.value)} placeholder="Staff key" aria-label="Staff key" autoFocus
                            style={{ background: "var(--hall)", color: "var(--ink)", border: "1px solid var(--line)", borderRadius: 6, padding: "12px 14px", fontSize: 16 }} />
                        <button type="submit">Start scanning</button>
                        <button type="button" onClick={() => setAskingKey(false)}>Cancel</button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ArrivalsPage;
